import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { NLGroupChat } from "@/components/your-voters/NLGroupChat";
import { VoterFileBrowser } from "@/components/voter-file/VoterFileBrowser";
import {
	validateVoterFileSearch,
	type VoterFileSearch,
} from "@/components/voter-file/voter-file-search-params";
import { useSession } from "@/hooks/use-session";
import { apiService } from "@/lib/api-service";

export const Route = createFileRoute("/_app/_/your-voters")({
	component: YourVotersPage,
	// Filter / sort / page state lives in the URL so it survives a refresh, is
	// shareable, and can be restored when the user closes a voter detail.
	validateSearch: validateVoterFileSearch,
});

function YourVotersPage() {
	const search = Route.useSearch();
	const navigate = Route.useNavigate();

	// Mirror the browser's state into the URL. `replace` so paging and filter
	// tweaks don't each push a history entry — the user should be able to hit
	// Back once to leave the page, and closing a voter detail should land on
	// the LATEST list state rather than replaying every intermediate filter.
	const handleSearchChange = useCallback(
		(next: VoterFileSearch) => {
			void navigate({ search: next, replace: true });
		},
		[navigate],
	);

	const contextSession = useSession();
	// Always refetch the session on this page so voter_file_access reflects
	// the latest admin toggle + impersonation state.
	const { data: freshSession } = useQuery({
		queryKey: ["session"],
		queryFn: () => apiService.getSession(),
		refetchOnMount: "always",
	});
	const session = freshSession ?? contextSession;
	const campaignId = session?.campaign?.id;
	const campaign = session?.campaign;
	const hasVoterFileAccess = campaign?.voter_file_access ?? false;

	if (!campaignId) {
		return (
			<div className="flex h-full items-center justify-center text-muted-foreground">
				No campaign selected
			</div>
		);
	}

	const lockedScope = {
		state: campaign?.voter_file_state ?? null,
		congressional_district: campaign?.voter_file_congressional_district ?? null,
		state_senate_district: campaign?.voter_file_state_senate_district ?? null,
		state_house_district: campaign?.voter_file_state_house_district ?? null,
		county: campaign?.voter_file_county ?? null,
		county_fips: campaign?.voter_file_county_fips ?? null,
		city: campaign?.voter_file_city ?? null,
		school_district: campaign?.voter_file_school_district ?? null,
	};

	if (!hasVoterFileAccess) {
		return (
			<div className="h-[calc(100vh-4rem)]">
				<NLGroupChat campaignId={campaignId} />
			</div>
		);
	}

	return (
		<div className="h-[calc(100vh-4rem)] overflow-auto">
			<VoterFileBrowser
				campaignId={campaignId}
				lockedScope={lockedScope}
				initialSearch={search}
				onSearchChange={handleSearchChange}
			/>
		</div>
	);
}
